package dev.kuml.portal.web.editor

import dev.kuml.portal.web.AppScope
import dev.kuml.portal.web.api.ApiResult
import dev.kuml.portal.web.api.DiagramResponse
import dev.kuml.portal.web.api.PortalApi
import dev.kuml.portal.web.i18n.t
import dev.kuml.portal.web.projects.DefaultNameDisplay
import io.kvision.core.Container
import io.kvision.core.onClick
import io.kvision.form.text.TextInput
import io.kvision.form.text.textInput
import io.kvision.html.Div
import io.kvision.html.button
import io.kvision.html.div
import io.kvision.html.span
import kotlinx.coroutines.launch

/**
 * @property setActive re-highlights the tab for [dev.kuml.portal.web.routing.Screen.Editor]'s
 *   currently open diagram -- called by `EditorScreen.kt` whenever the active diagram id changes
 *   (e.g. after a diagram switch has already navigated).
 * @property reload re-fetches the diagram list from the server -- called after a rename/delete/
 *   create elsewhere in the editor changes it, mirroring [dev.kuml.portal.web.projects.projectListScreen]'s
 *   own `reload()` convention.
 */
public class DiagramTabsHandle(
    public val setActive: (String) -> Unit,
    public val reload: () -> Unit,
)

/**
 * R-22's horizontal diagram-tab bar, mounted directly under [dev.kuml.portal.web.editor.editorScreen]'s
 * existing `kportal-editor-subheader` strip (see that function's own layout). Chosen over a
 * sidebar (would break the existing two-column `kportal-editor-grid`'s narrow-viewport stacking,
 * which nothing else in this portal has a pattern for) or a dropdown (hides the exact information
 * -- "which diagrams exist" -- that motivates switching in the first place); see the plan this
 * wave was built from for the full design-team review.
 *
 * Every tab is built ENTIRELY out of KVision child-widget calls (`div { span(...); button(...) }`),
 * never `content =`/`content +=` mixed with child widgets in the same container -- this codebase's
 * binding convention (diagram names are server-persisted, user-controlled free text).
 *
 * Overflow: a plain `overflow-x: auto` on the host div (see `app.css`) -- no dropdown fallback, the
 * per-project diagram cap (`Plan.maxDiagramsPerProject`, 3 on Free) never gets large enough to need
 * one.
 *
 * @param onSwitch called with a tab's diagram id when clicked (never for the already-active tab) --
 *   the caller navigates via the router, which remounts the whole editor screen (see `PortalApp.kt`'s
 *   `renderScreen`), so this widget itself never needs to reload the stage.
 * @param onError forwarded straight to the editor's existing status area (`setStatusError`) so a
 *   failed rename/delete/create surfaces exactly like every other editor error.
 */
public fun Container.diagramTabs(
    api: PortalApi,
    projectId: String,
    activeDiagramId: String,
    onSwitch: (String) -> Unit,
    onError: (message: String, detail: String?) -> Unit,
): DiagramTabsHandle {
    lateinit var tabsHost: Div
    var currentActiveId = activeDiagramId
    var currentDiagrams: List<DiagramResponse> = emptyList()

    val root = div(className = "kportal-diagram-tabs")
    tabsHost = root

    fun renderTab(
        host: Div,
        diagram: DiagramResponse,
        onChanged: () -> Unit,
    ) {
        val isActive = diagram.id == currentActiveId
        val tab = host.div(className = if (isActive) "kportal-diagram-tab kportal-diagram-tab-active" else "kportal-diagram-tab")
        val displayName = DefaultNameDisplay.diagramName(diagram.name, t.editor)
        val nameSpan = tab.span(displayName, className = "kportal-diagram-tab-name")
        if (!isActive) {
            nameSpan.onClick { onSwitch(diagram.id) }
        }

        val renameButton = tab.button("✎", className = "kportal-icon-button kportal-diagram-tab-action")
        renameButton.setAttribute("aria-label", t.editor.tabRenameAriaLabel)
        val deleteButton = tab.button("🗑", className = "kportal-icon-button kportal-diagram-tab-action")
        deleteButton.setAttribute("aria-label", t.editor.tabDeleteAriaLabel)
        val popoverHost = tab.div()

        renameButton.onClick { e ->
            e.stopPropagation()
            startInlineRename(tab, nameSpan, diagram, api, onChanged)
        }
        deleteButton.onClick { e ->
            e.stopPropagation()
            showDeleteConfirmation(popoverHost, diagram, currentDiagrams.size, api, onChanged, onError)
        }
    }

    fun reload() {
        AppScope.launch {
            when (val result = api.listDiagrams(projectId)) {
                is ApiResult.Success -> {
                    currentDiagrams = result.value
                    tabsHost.removeAll()
                    result.value.forEach { diagram -> renderTab(tabsHost, diagram) { reload() } }
                    val plusTab = tabsHost.button("+", className = "kportal-diagram-tab kportal-diagram-tab-add")
                    plusTab.setAttribute("aria-label", t.editor.tabNewAriaLabel)
                    plusTab.onClick {
                        AppScope.launch {
                            when (val created = api.createDiagram(projectId)) {
                                is ApiResult.Success -> onSwitch(created.value.id)
                                is ApiResult.HttpError -> onError(diagramLimitOrGenericMessage(created.status), null)
                                ApiResult.NetworkError -> onError(t.common.networkErrorRetry, null)
                            }
                        }
                    }
                }
                is ApiResult.HttpError -> onError(t.editor.tabListLoadFailed, null)
                ApiResult.NetworkError -> onError(t.common.networkErrorReload, null)
            }
        }
    }

    reload()

    return DiagramTabsHandle(
        setActive = { id ->
            currentActiveId = id
            reload()
        },
        reload = { reload() },
    )
}

/** Free plan's hard cap on diagrams per project -- mirrors the server's `Plan.maxDiagramsPerProject`
 * for the 402 copy's "(max 3)" fragment; only ever used to fill in this ONE well-known status. */
private const val FREE_PLAN_DIAGRAM_LIMIT = 3

private fun diagramLimitOrGenericMessage(status: Int): String =
    if (status == 402) t.editor.tabDiagramLimitReached(FREE_PLAN_DIAGRAM_LIMIT) else t.editor.tabCreateFailed

/** Stift -> inline textfield (Enter speichert per `PATCH`, Escape verwirft, Blur speichert) --
 * mirrors `ProjectListScreen.kt`'s `startInlineRename`/`EditorScreen.kt`'s `startRename` pattern. */
private fun startInlineRename(
    tab: Div,
    nameSpan: io.kvision.html.Span,
    diagram: DiagramResponse,
    api: PortalApi,
    onChanged: () -> Unit,
) {
    val original = diagram.name
    nameSpan.visible = false
    val input = tab.textInput(value = original, className = "kportal-input kportal-diagram-tab-input")
    var settled = false

    fun cancel() {
        if (settled) return
        settled = true
        input.getElement()?.remove()
        nameSpan.visible = true
    }

    fun save() {
        if (settled) return
        val newName = input.value?.trim().orEmpty()
        if (newName.isEmpty() || newName == original) {
            cancel()
            return
        }
        settled = true
        AppScope.launch {
            when (api.renameDiagram(diagram.id, newName)) {
                is ApiResult.Success -> onChanged()
                is ApiResult.HttpError, ApiResult.NetworkError -> {
                    // Rename failed server-side (e.g. a name collision) -- revert the tab rather
                    // than leaving it displaying a name that was never actually stored.
                    onChanged()
                }
            }
        }
    }

    input.setEventListener<TextInput> {
        keydown = { e ->
            when (e.key) {
                "Enter" -> {
                    e.preventDefault()
                    save()
                }
                "Escape" -> {
                    e.preventDefault()
                    cancel()
                }
            }
        }
        blur = { save() }
    }
}

/** Mirrors `ProjectListScreen.kt`'s `showDeleteConfirmation` popover pattern -- the one
 * confirmation dialog allowed anywhere in this UI. Surfaces the server's `last_diagram`/
 * `diagram_live_room` conflict codes with their approved copy instead of a generic error. */
private fun showDeleteConfirmation(
    host: Div,
    diagram: DiagramResponse,
    diagramCount: Int,
    api: PortalApi,
    onChanged: () -> Unit,
    onError: (message: String, detail: String?) -> Unit,
) {
    host.removeAll()
    if (diagramCount <= 1) {
        onError(t.editor.tabMinOneDiagram, t.editor.tabMinOneDiagramDetail)
        return
    }
    val displayName = DefaultNameDisplay.diagramName(diagram.name, t.editor)
    val popover = host.div(className = "kportal-popover")
    popover.onClick { e -> e.stopPropagation() }
    popover.span(t.editor.tabDeleteConfirmQuestion(displayName))
    val errorSpan = popover.span("", className = "kportal-status-error")
    errorSpan.visible = false
    val actions = popover.div(className = "kportal-popover-actions")
    val cancel = actions.button(t.common.cancel, className = "kportal-btn kportal-btn-secondary kportal-btn-auto")
    val confirm = actions.button(t.common.delete, className = "kportal-btn kportal-btn-text")
    cancel.onClick { e ->
        e.stopPropagation()
        host.removeAll()
    }
    confirm.onClick { e ->
        e.stopPropagation()
        confirm.disabled = true
        cancel.disabled = true
        AppScope.launch {
            when (val result = api.deleteDiagram(diagram.id)) {
                is ApiResult.Success -> onChanged()
                is ApiResult.HttpError -> {
                    confirm.disabled = false
                    cancel.disabled = false
                    val message =
                        when (result.error?.code) {
                            "last_diagram" -> t.editor.tabMinOneDiagram + " " + t.editor.tabMinOneDiagramDetail
                            "diagram_live_room" -> t.editor.tabLiveRoom
                            else -> t.editor.tabDeleteFailed
                        }
                    errorSpan.content = message
                    errorSpan.visible = true
                }
                ApiResult.NetworkError -> {
                    confirm.disabled = false
                    cancel.disabled = false
                    errorSpan.content = t.common.networkErrorRetry
                    errorSpan.visible = true
                }
            }
        }
    }
}
